/**
 * Pagination.jsx
 *
 * @Author: jruif
 * @Date: 2017/7/14 上午11:44
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '../button';
import Input from '../input';
import scrollTo from '../util/scrollTo';
import Select from '../select';

/**
 * Pagination
 * */
class Pagination extends React.Component {
  static propTypes = {
    /**
     * 数据总数，和totalPage二选一
     * */
    total: PropTypes.number,
    /**
     * 页数总数，和total二选一
     * */
    totalPage: PropTypes.number,
    /**
     * 每页大小
     * */
    pageSize: PropTypes.number,
    /**
     * 当前页码
     * */
    current: PropTypes.number,
    /**
     * 分页数组，例如：[10, 20, 40]
     * */
    pageSizeOptions: PropTypes.array,
    /**
     * 是否显示快速跳转
     * */
    showQuickJump: PropTypes.bool,
    /**
     * 是否显示每一页，为false的时候，只显示上下翻页按钮
     * */
    showPer: PropTypes.bool,
    /**
     * 是否数据总条数
     * */
    showTotal: PropTypes.bool,
    /**
     * 当只有一页的时候是否显示分页
     * */
    showWhenOnlyOne: PropTypes.bool,
    /**
     * 是否显示选择分页选择器
     * */
    showSelectPageSize: PropTypes.bool,
    /**
     * 是否显示为紧凑形式
     * */
    compact: PropTypes.bool,
    /**
     * 跳转页面，返回目标页码
     * */
    onChange: PropTypes.func,
    /**
     * 跳转输入框的change回调
     * */
    onInput: PropTypes.func,
    /**
     * 改变分页大小，返回改变后的数值
     * */
    onSizeChange: PropTypes.func,
    /**
     * 是否跳到页面顶部
     * */
    scrollToTop: PropTypes.bool,
    /**
     * 缓动动画名，参考：http://easings.net/zh-cn
     * */
    tweenName: PropTypes.string,
    /**
     * 滚动时间，单位是ms
     * */
    scrollTime: PropTypes.number, // 800秒
    /**
     * 滚动到某一点坐标
     * */
    scrollToValue: PropTypes.number,
    /**
     * 分页中所有按钮的属性，同Button组件
     * */
    buttonProps: PropTypes.object,
    /**
     * 优先级低于buttonProps.size
     * */
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    /**
     * 前一页按钮
     * */
    prev: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /**
     * 后一页按钮
     * */
    next: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /**
     * 组件前缀，可用来重置样式
     * */
    prefixCls: PropTypes.string,
    /**
     * 分页样式类
     * */
    className: PropTypes.string,
  };

  static defaultProps = {
    total: 0,
    totalPage: 0,
    pageSize: 10,
    current: 1,
    showPer: true,
    compact: false,
    showQuickJump: false,
    showTotal: false,
    showSelectPageSize: false,
    showWhenOnlyOne: false,
    prefixCls: 'ne-pagination',
    scrollToTop: true,
    scrollTime: 800,
    scrollToValue: 0,
    tweenName: 'easeOutQuint',
    size: 'medium',
    buttonProps: {},
    prev: '上一页',
    next: '下一页',
    pageSizeOptions: [10, 20, 50, 100],
  };

  static getDerivedStateFromProps(props, state) {
    let newState = null;
    const { current, pageSize } = props;
    if (current !== state.prevCurrent) {
      newState = {
        current,
        prevCurrent: current,
      };
    }
    if (pageSize !== state.prevPageSize) {
      newState = Object.assign({}, newState, {
        pageSize,
        prevPageSize: pageSize,
      });
      const { total, totalPage } = props;
      let totalNumber = 0;

      if (totalPage === 0) {
        if (total !== 0) {
          totalNumber = Math.ceil(total / pageSize);
        }
      } else {
        totalNumber = totalPage;
      }
      const maxPage = totalNumber;
      const currentPage = newState.current || state.current;
      newState.current = Math.min(currentPage, maxPage);
    }
    return newState;
  }

  constructor(props) {
    super(props);
    const { current, pageSize } = props;
    this.state = {
      current,
      prevCurrent: current,
      pageSize,
      prevPageSize: pageSize,
      inputPage: '',
    };
  }

  handleSizeChange = (e, value) => {
    const { onChangePageSize, onSizeChange } = this.props;
    this.setState(state => {
      const newState = {
        pageSize: value,
      };
      const maxPage = this.calcPage(value);
      const currentPage = state.current;
      newState.current = Math.min(currentPage, maxPage);
      return newState;
    });
    this.scrollToTop();
    if (onSizeChange) onSizeChange(value);
    if (onChangePageSize) onChangePageSize(value);
  };

  getTotalPage = () => {
    const { total, pageSize, totalPage } = this.props;
    return totalPage || Math.ceil(total / pageSize);
  };

  handleInput = e => {
    const { onChangePage, onInput } = this.props;
    const { target } = e;
    const value = Number(target.value);
    const totalPage = this.getTotalPage();
    const validValue = isNaN(value) || value < 1 ? '' : value > totalPage ? totalPage : value;
    this.setState({
      inputPage: validValue,
    });
    if (onChangePage) onChangePage(validValue);
    if (onInput) onInput(validValue);
  };

  quickJumpPage = () => {
    const { inputPage } = this.state;
    if (inputPage > 0 && inputPage <= this.getTotalPage()) {
      this.jumpPage(inputPage);
    }
  };

  jumpPage = next => {
    const { onChange } = this.props;
    this.setState({
      current: next,
    });
    this.scrollToTop(() => {
      onChange && onChange(next);
    });
  };

  scrollToTop = callback => {
    const { scrollToTop, scrollTime, tweenName, scrollToValue } = this.props;
    if (scrollToTop) {
      const { pageYOffset } = window;
      scrollTo(pageYOffset, scrollToValue, scrollTime, tweenName, () => {
        callback && callback();
      });
    } else {
      callback && callback();
    }
  };

  prev = (page = 1) => {
    const { current } = this.state;
    if (current > 1) {
      this.jumpPage(current - page);
    }
  };

  next = (page = 1) => {
    const { current } = this.state;
    if (this.calcPage() - current > 0) {
      this.jumpPage(current + page);
    }
  };

  calcPage = p => {
    let size = p;
    const { total, totalPage } = this.props;
    const { pageSize } = this.state;
    let totalNumber = 0;
    // 有默认值
    if (size == null) {
      size = pageSize;
    }
    if (totalPage === 0) {
      if (total !== 0) {
        totalNumber = Math.ceil(total / size);
      }
    } else {
      totalNumber = totalPage;
    }
    return totalNumber;
  };

  renderPreBtn = index => {
    const { buttonProps, prefixCls, size } = this.props;
    const { current } = this.state;
    const isCurrent = index === current;
    return (
      <Button
        variant="outlined"
        size={size}
        {...buttonProps}
        className={classNames({
          [`${prefixCls}-button-active`]: isCurrent,
        })}
        onClick={() => this.jumpPage(index)}
        color={isCurrent ? 'primary' : 'secondary'}>
        {index}
      </Button>
    );
  };

  render() {
    let { total, totalPage } = this.props;

    const {
      showTotal,
      showQuickJump,
      showSelectPageSize,
      prefixCls,
      showWhenOnlyOne,
      size,
      buttonProps,
      prev,
      next,
      className,
      showPer,
      compact,
      isCompact,
      pageSizeOptions,
    } = this.props;

    let { pageSize } = this.state;
    const { current, inputPage } = this.state;

    const elementPage = [];

    totalPage = Number(totalPage);
    total = Number(total);
    pageSize = Number(pageSize);

    if (totalPage === 0 && total === 0) {
      return null;
    }
    if (total !== 0) {
      // 默认值为0
      totalPage = Math.ceil(total / pageSize);
    }
    if (totalPage < 9) {
      for (let i = 0; i < totalPage; i++) {
        elementPage.push(<li key={`page-${i}`}>{this.renderPreBtn(i + 1)}</li>);
      }
    } else {
      let left = Math.max(1, current - 2);
      let right = Math.min(current + 2, totalPage);
      if (current - 1 <= 2) {
        right = 1 + 4;
      }

      if (totalPage - current <= 2) {
        left = totalPage - 4;
      }

      for (let i = left; i <= right; i++) {
        elementPage.push(<li key={`page-${i}`}>{this.renderPreBtn(i)}</li>);
      }
      if (current - 1 >= 4) {
        elementPage.unshift(
          <li key="ellipsis-1">
            <Button
              variant="outlined"
              className={classNames(`${prefixCls}-ellipsis-prev`, {
                [buttonProps.className]: buttonProps.className,
              })}
              size={size}
              onClick={() => this.prev(4)}
              {...buttonProps}>
              ...
            </Button>
          </li>,
        );
      }
      if (totalPage - current >= 4) {
        elementPage.push(
          <li key="ellipsis-2">
            <Button
              variant="outlined"
              className={classNames(`${prefixCls}-ellipsis-next`, {
                [buttonProps.className]: buttonProps.className,
              })}
              size={size}
              onClick={() => this.next(4)}
              {...buttonProps}>
              ...
            </Button>
          </li>,
        );
      }
      if (left !== 1) {
        elementPage.unshift(<li key="page-first">{this.renderPreBtn(1)}</li>);
      }
      if (right !== totalPage) {
        elementPage.push(<li key="page-last">{this.renderPreBtn(totalPage)}</li>);
      }
    }

    const classes = classNames(prefixCls, className, `${prefixCls}-${size}`, {
      [`${prefixCls}-compact`]: isCompact || compact,
    });

    return this.calcPage() <= 1 && !showWhenOnlyOne ? null : (
      <ul className={classes}>
        <li className={`${prefixCls}-previous`}>
          <Button
            variant="outlined"
            onClick={() => this.prev(1)}
            disabled={current <= 1}
            size={size}
            {...buttonProps}>
            {prev}
          </Button>
        </li>
        {showPer ? elementPage : null}
        <li className={`${prefixCls}-next`}>
          <Button
            variant="outlined"
            size={size}
            onClick={() => this.next(1)}
            disabled={!(this.calcPage() - current > 0)}
            {...buttonProps}>
            {next}
          </Button>
        </li>
        {showSelectPageSize && (
          <li>
            <Select
              className={`${prefixCls}-select`}
              defaultValue={pageSize}
              onChange={this.handleSizeChange}
              size={size}
              options={pageSizeOptions.map(val => ({
                label: `${val}条/页`,
                value: val,
              }))}/>
          </li>
        )}
        {!!total && showTotal && (
          <li>
            <span className={`${prefixCls}-total`}>共 {total} 条</span>
          </li>
        )}
        {showQuickJump && (
          <li className={`${prefixCls}-number`}>
            第
            <Input
              size={size}
              className={`${prefixCls}-input`}
              type="number"
              onChange={this.handleInput}
              value={inputPage}/>
            页
            <Button
              className={`${prefixCls}-jump`}
              size={size}
              onClick={this.quickJumpPage}
              {...buttonProps}>
              跳转
            </Button>
          </li>
        )}
      </ul>
    );
  }
}

export default Pagination;



// WEBPACK FOOTER //
// ./src/pagination/Pagination.jsx